import { IMAGES } from "../lib/site";

// Distinction between advisory, documentation, filing assistance and
// representation is stated per service, as required for ethical presentation.
export const SERVICES = [
  {
    slug: "corporate-commercial",
    num: "01",
    title: "Corporate & Commercial Law",
    shortTitle: "Corporate & Commercial",
    summary:
      "Corporate legal advisory, commercial transactions and business documentation for companies at every stage.",
    image: IMAGES.boardroom,
    intro:
      "Sound corporate legal advice is less about precedent and more about consequence — understanding what a transaction, an agreement or a governance decision will mean for the business two years on. We advise businesses on their corporate and commercial legal needs with that commercial lens.",
    points: [
      { t: "Corporate legal advisory", d: "Practical advice on governance, directors' duties, shareholding matters and corporate decisions under the Companies Act, 2013." },
      { t: "Commercial transactions", d: "Structuring and documenting business acquisitions, investments, collaborations and day-to-day commercial arrangements." },
      { t: "Business documentation", d: "Statutory records, board and shareholder documentation, registers and resolutions — prepared properly, the first time." },
      { t: "Distribution and service agreements", d: "Distribution, franchise, reseller and service arrangements that protect margins, territory and brand." },
      { t: "Non-disclosure agreements", d: "One-way and mutual NDAs drafted for real enforceability, matched to the flow of information in the deal." },
      { t: "Vendor and business agreements", d: "Vendor onboarding, procurement and business partnership agreements with clear risk allocation." },
    ],
    engagement: [
      { label: "Legal advisory", d: "We advise you directly on your rights, obligations and options." },
      { label: "Documentation", d: "We draft, review and negotiate the agreements and records." },
      { label: "Filing assistance", d: "Where filings with authorities are involved, we prepare and assist with the filings." },
      { label: "Representation", d: "Not applicable to this service in most matters; referred out where appropriate, with your consent." },
    ],
  },
  {
    slug: "ip-trademark",
    num: "02",
    title: "Intellectual Property & Trademark",
    shortTitle: "IP & Trademark",
    summary:
      "Trademark searches, filing, examination replies, oppositions, renewals and brand protection strategy.",
    image: IMAGES.discussion,
    intro:
      "A brand is a promise customers rely on, and trademark registration is how the law lets you keep that promise exclusive. We manage the full lifecycle of trademark protection in India — from the first availability search to renewals a decade later.",
    points: [
      { t: "Trademark availability searches", d: "Preliminary and comprehensive searches of the Registry database for identical and deceptively similar marks, with a candid view on registrability." },
      { t: "Trademark application filing", d: "Applications drafted with the right specification, class coverage and user claim, filed with the Trade Marks Registry." },
      { t: "Examination report replies", d: "Considered written responses to objections raised by the Registry, including hearings where required." },
      { t: "Trademark objections and oppositions", d: "Responding to objections and third-party oppositions — and managing oppositions against conflicting marks." },
      { t: "Renewal and portfolio management", d: "Diary management of renewal deadlines and consolidated administration of multi-class and multi-application portfolios." },
      { t: "Brand protection strategy", d: "A practical roadmap covering what to register, what to monitor, and how to respond to infringement." },
      { t: "IP documentation", d: "Assignments, licences, and brand-related documentation for transactions and internal governance." },
    ],
    engagement: [
      { label: "Legal advisory", d: "Candid assessment of availability, registrability and enforcement options." },
      { label: "Documentation", d: "Drafting of applications, replies, assignments and licensing documents." },
      { label: "Filing assistance", d: "We prepare and file applications and responses with the Trade Marks Registry on your behalf." },
      { label: "Representation", d: "Representation in Registry hearings and, where court proceedings are required, coordination with counsel." },
    ],
  },
  {
    slug: "contract-drafting",
    num: "03",
    title: "Contract Drafting & Management",
    shortTitle: "Contracts",
    summary:
      "Drafting, reviewing and negotiating contracts — and managing them through their lifecycle.",
    image: IMAGES.contract,
    intro:
      "Contracts are how businesses set expectations in writing. Done well, they prevent disputes; done carelessly, they finance them. We draft, review and negotiate commercial contracts with a focus on clarity, enforceability and the risks that actually matter to your deal.",
    points: [
      { t: "Drafting, reviewing and negotiating", d: "New contracts drafted from the deal up, and counterparty paper reviewed line by line with flagged risks and suggested revisions." },
      { t: "Lease and rental agreements", d: "Commercial and residential lease documentation that addresses rent, deposit, exit and repair obligations clearly." },
      { t: "Service-level agreements", d: "SLAs with measurable performance standards, cure periods and consequences that can actually be operated." },
      { t: "Commercial and vendor contracts", d: "Purchase, supply, outsourcing and partnership agreements with balanced indemnities and liability caps." },
      { t: "Contract lifecycle management", d: "Tracking obligations, renewals, notice periods and escalations across a portfolio of live contracts." },
      { t: "Risk identification and compliance", d: "Pre-signature risk reviews and contractual compliance checks against your internal policies and statutory duties." },
    ],
    engagement: [
      { label: "Legal advisory", d: "We explain the risks in plain language before you commit." },
      { label: "Documentation", d: "Full drafting and negotiation support is the core of this service." },
      { label: "Filing assistance", d: "Registration of lease documentation where the applicable law requires it." },
      { label: "Representation", d: "Not applicable; disputes arising from contracts are handled under dispute resolution, with your consent." },
    ],
  },
  {
    slug: "real-estate-property",
    num: "04",
    title: "Real Estate & Property Law",
    shortTitle: "Real Estate",
    summary:
      "Property due diligence, title verification, lease documentation and property transaction support.",
    image: IMAGES.highrise,
    intro:
      "Property is where documentation discipline matters most — the difference between a secure acquisition and a future dispute is almost always in the title records. We investigate before you commit, and document the transaction so the record stays clean.",
    points: [
      { t: "Property due diligence", d: "A structured investigation of title, encumbrances, litigation, permissions and dues before you purchase, lease or lend." },
      { t: "Title verification and title reports", d: "Verification of the chain of ownership against statutory periods, delivered as a written title report with defects and cures stated." },
      { t: "Lease and leave-and-license documentation", d: "Drafting and registration of lease deeds and leave-and-license agreements for landlords and tenants." },
      { t: "Property transaction documentation", d: "Sale deeds, agreements to sell, powers of attorney and related documents drafted and reviewed for registration." },
      { t: "Legal review of property records", d: "Independent review of existing records — for owners, buyers and lenders — to establish exactly where the title stands." },
    ],
    engagement: [
      { label: "Legal advisory", d: "Candid views on title quality and transaction structure." },
      { label: "Documentation", d: "Drafting and review of all transaction and lease documentation." },
      { label: "Filing assistance", d: "Registration support before the sub-registrar, including document preparation and formalities." },
      { label: "Representation", d: "Not applicable to transactions; property disputes are handled under dispute resolution, with your consent." },
    ],
  },
  {
    slug: "regulatory-compliance",
    num: "05",
    title: "Regulatory & Legal Compliance",
    shortTitle: "Compliance",
    summary:
      "Trade licences, regulatory approvals, compliance reviews and statutory documentation support.",
    image: IMAGES.handshake,
    intro:
      "Every premises, activity and industry in India carries its own layer of licences and approvals — and regulators enforce the layer you forgot. We help businesses identify what applies, obtain it, and keep it current.",
    points: [
      { t: "Business and trade licence support", d: "Identification and procurement of the municipal and state licences your business and premises require." },
      { t: "Regulatory approvals", d: "Preparation, submission and follow-up of applications to regulatory authorities and departments." },
      { t: "Legal compliance reviews", d: "A structured audit of your obligations — licences, filings, records and renewals — with a prioritised action list." },
      { t: "Retail, warehouse, factory and office compliance", d: "Premises-specific compliance for shops and establishments, storage, manufacturing and office operations." },
      { t: "Statutory documentation and compliance tracking", d: "Registers, renewal calendars and documentation systems that keep compliance current, not merely achieved once." },
    ],
    engagement: [
      { label: "Legal advisory", d: "We tell you what applies to your activity and premises — and what does not." },
      { label: "Documentation", d: "Preparation of applications, representations and statutory records." },
      { label: "Filing assistance", d: "We prepare and assist with filings before the relevant authorities." },
      { label: "Representation", d: "Follow-up and representation before licensing authorities as applicable, and coordination with counsel for proceedings." },
    ],
  },
  {
    slug: "litigation-dispute",
    num: "06",
    title: "Litigation & Dispute Resolution",
    shortTitle: "Disputes",
    summary:
      "Legal notices, recovery matters, commercial disputes and coordinated litigation support.",
    image: IMAGES.discussion,
    intro:
      "Disputes reward preparation and punish delay. Whether the matter needs a firm legal notice, a negotiated settlement or a filed case, we assess the position early, document it properly and pursue the route that actually serves the commercial objective.",
    points: [
      { t: "Legal notices and recovery matters", d: "Statutory and demand notices, and structured recovery of commercial dues — including cheque dishonour cases." },
      { t: "Commercial disputes", d: "Disputes arising from contracts, partnerships, supply and services, assessed for merits, limitation and strategy." },
      { t: "Cheque dishonour matters", d: "End-to-end handling of proceedings under Section 138 of the Negotiable Instruments Act — notice, complaint and settlement." },
      { t: "Employment and labour disputes", d: "Advisory and dispute support on employment terminations, dues, notice and labour-law compliance." },
      { t: "Coordination with advocates", d: "Where a matter requires appearance before courts or tribunals, we prepare the record and coordinate with instructed advocates." },
      { t: "Dispute resolution and settlement support", d: "Negotiated settlements, consent terms and structured exits — often the fastest and cheapest resolution available." },
    ],
    engagement: [
      { label: "Legal advisory", d: "A frank assessment of merits, limitation and the realistic range of outcomes." },
      { label: "Documentation", d: "Notices, pleadings support, settlement terms and the litigation record." },
      { label: "Filing assistance", d: "Preparation and filing of complaints and applications as applicable." },
      { label: "Representation", d: "Matters requiring court appearance are conducted with instructed advocates, with documentation and strategy coordinated end to end." },
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
